import React, { useCallback, useState } from 'react'

import { HiChevronUpDown } from 'react-icons/hi2'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { getComboboxTriggerClassName } from '@/components/library-v2/ui/Combobox/lib'
import {
  EntityAvatar,
  getEntityLabel,
  getEntityName,
} from '@/components/library-v2/ui/Entity'

import { useEntities } from '@/hooks/library/useEntities'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Entity } from '@/types/library/Entity'

// region: utils

const sortEntitiesByName = (entities: Entity[]) => {
  return entities.slice().sort((a: Entity, b: Entity) => {
    const aName = getEntityName(a)
    const bName = getEntityName(b)
    if (aName.length === 0) return 1
    if (bName.length === 0) return -1
    return aName.localeCompare(bName)
  })
}

const selectSortedEntities = (data: PaginatedResponse<Entity>) => {
  return sortEntitiesByName(data.data)
}

// endregion: utils

interface EntityComboboxProps {
  value: UUID | null
  onChange: (entityId: UUID) => void
  onClear?: () => void
  entities?: Entity[]
  placeholder?: string
  disabled?: boolean
}

export const EntityCombobox: React.FC<EntityComboboxProps> = ({
  value,
  onChange,
  onClear,
  entities,
  placeholder = 'Select entity...',
  disabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = useCallback(
    (entityId: UUID) => () => {
      onChange(entityId)
      setOpen(false)
    },
    [onChange],
  )

  const handleClear = useCallback(() => {
    onClear?.()
    setOpen(false)
  }, [onClear])

  const selected = entities?.find((e) => e.id === value) || null

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={getComboboxTriggerClassName({ disabled })}
        >
          {selected ? (
            <div className="flex items-center gap-2 truncate min-w-0">
              <EntityAvatar entity={selected} size={18} />
              <div className="min-w-0">
                <div className="text-sm truncate">
                  {getEntityName(selected)}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
          <HiChevronUpDown size={16} className="flex-shrink-0" />
        </button>
      </ComboboxTrigger>
      <ComboboxContent align="start" className="p-0 w-[360px]">
        <ComboboxInput
          placeholder="Search..."
          className="placeholder:text-sm"
        />
        <ComboboxList>
          <ComboboxEmpty className="text-sm">No results found.</ComboboxEmpty>
          <ComboboxGroup>
            {value && onClear ? (
              <ComboboxSelectableItem
                key="__clear__"
                value="__clear__"
                isSelected={false}
                onSelect={handleClear}
              >
                <span className="text-sm text-red-600 dark:text-red-400">
                  Clear selection
                </span>
              </ComboboxSelectableItem>
            ) : null}
            {entities?.map((entity) => {
              const name = getEntityName(entity)
              return (
                <ComboboxSelectableItem
                  key={entity.id}
                  value={`${name}-${entity.id}`}
                  isSelected={value === entity.id}
                  onSelect={handleSelect(entity.id)}
                >
                  <EntityRow entity={entity} />
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

interface AllOrganizationEntitiesComboboxProps {
  value: UUID | null
  onChange: (entityId: UUID) => void
  confirmedOnly?: boolean
  placeholder?: string
  disabled?: boolean
}

export const AllOrganizationEntitiesCombobox: React.FC<
  AllOrganizationEntitiesComboboxProps
> = ({
  value,
  onChange,
  confirmedOnly = false,
  placeholder = 'Select...',
  disabled = false,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: entities } = useEntities({
    orgId: activeOrgId,
    withSuggested: confirmedOnly ? false : undefined,
    select: selectSortedEntities,
  })

  return (
    <EntityCombobox
      value={value}
      onChange={onChange}
      entities={entities}
      placeholder={placeholder}
      disabled={disabled}
    />
  )
}

interface EntityComboboxMultiProps {
  value: UUID[]
  onChange: (entityIds: UUID[]) => void
  entities?: Entity[]
  placeholder?: string
  disabled?: boolean
}

export const EntityComboboxMulti: React.FC<EntityComboboxMultiProps> = ({
  value,
  onChange,
  entities,
  placeholder = 'Select...',
  disabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleToggle = useCallback(
    (entityId: UUID) => () => {
      const isSelected = value.includes(entityId)
      if (isSelected) {
        onChange(value.filter((id) => id !== entityId))
      } else {
        onChange([...value, entityId])
      }
    },
    [onChange, value],
  )

  const selectedEntities = entities?.filter((e) => value.includes(e.id)) || []

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={getComboboxTriggerClassName({ disabled })}
        >
          {selectedEntities.length > 0 ? (
            <div className="flex items-center gap-2 truncate min-w-0">
              {selectedEntities.length === 1 ? (
                <EntityRow entity={selectedEntities[0]} compact={true} />
              ) : (
                <span className="text-sm truncate">{`${selectedEntities.length} selected`}</span>
              )}
            </div>
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
          <HiChevronUpDown size={16} className="flex-shrink-0" />
        </button>
      </ComboboxTrigger>
      <ComboboxContent align="start" className="p-0 w-[360px]">
        <ComboboxInput
          placeholder="Search..."
          className="placeholder:text-sm"
        />
        <ComboboxList>
          <ComboboxEmpty className="text-sm">No results found.</ComboboxEmpty>
          <ComboboxGroup>
            {entities?.map((entity) => {
              const name = getEntityName(entity)
              const isSelected = value.includes(entity.id)
              return (
                <ComboboxSelectableItem
                  key={entity.id}
                  value={`${name}-${entity.id}`}
                  isSelected={isSelected}
                  onSelect={handleToggle(entity.id)}
                >
                  <EntityRow entity={entity} />
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

interface AllOrganizationEntitiesComboboxMultiProps {
  value: UUID[]
  onChange: (entityIds: UUID[]) => void
  confirmedOnly?: boolean
  placeholder?: string
  disabled?: boolean
}

export const AllOrganizationEntitiesComboboxMulti: React.FC<
  AllOrganizationEntitiesComboboxMultiProps
> = ({
  value,
  onChange,
  confirmedOnly = false,
  placeholder = 'Select...',
  disabled = false,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: entities } = useEntities({
    orgId: activeOrgId,
    withSuggested: confirmedOnly ? false : undefined,
    select: selectSortedEntities,
  })

  return (
    <EntityComboboxMulti
      value={value}
      onChange={onChange}
      entities={entities}
      placeholder={placeholder}
      disabled={disabled}
    />
  )
}

interface EntityRowProps {
  entity: Entity
  compact?: boolean
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, compact = false }) => {
  return (
    <div className="flex items-center gap-3 truncate min-w-0">
      <EntityAvatar entity={entity} size={compact ? 26 : 28} />
      <div className="min-w-0 inline-block leading-tight">
        <div className="text-sm truncate">{getEntityName(entity)}</div>
        {!compact && (
          <div className="text-[13px] text-primary-500 dark:text-primary-400 truncate">
            {getEntityLabel(entity)}
          </div>
        )}
      </div>
    </div>
  )
}

export const EntitySelect: React.FC<EntityComboboxProps> = ({
  value,
  onChange,
  entities,
  placeholder = 'Select...',
  disabled = false,
}) => {
  const handleValueChange = useCallback(
    (id: string) => {
      onChange(id as UUID)
    },
    [onChange],
  )

  const selected = entities?.find((e) => e.id === value) || null

  return (
    <Select
      value={value || ''}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-full dark:bg-default-dark">
        <SelectValue placeholder={placeholder}>
          {selected ? (
            <EntityRow entity={selected} compact={true} />
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {entities && entities.length > 0 ? (
          entities.map((e) => (
            <SelectItem key={e.id} value={e.id}>
              <EntityRow entity={e} compact={true} />
            </SelectItem>
          ))
        ) : (
          <div className="text-sm truncate px-2 py-[5px] text-primary-500 dark:text-primary-400">
            <span className="text-sm truncate">No results found.</span>
          </div>
        )}
      </SelectContent>
    </Select>
  )
}
